import { lazy, Suspense, useEffect, useState, useMemo } from "react";
import { FleetChrome, IconSprite, type NavigationConfig, type NavItem } from "@weretradeit/ui-react";
import { useSecurityAuth } from "./api/authClient";

// Lazy load page components for code splitting
const HomePage = lazy(() => import("./pages/HomePage"));
const CveFleetPage = lazy(() => import("./pages/CveFleetPage"));
const ScanWizardPage = lazy(() => import("./pages/ScanWizardPage"));
const ScanResultsPage = lazy(() => import("./pages/ScanResultsPage"));
const ScanHistoryPage = lazy(() => import("./pages/ScanHistoryPage"));
const HoneypotDashboardPage = lazy(() => import("./pages/HoneypotDashboardPage"));

// Loading fallback for lazy routes
const PageLoader = () => (
  <div className="loading-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: '1rem', color: 'var(--text-secondary, #94a3b8)' }}>
    <div className="loading-spinner" style={{ width: '36px', height: '36px', border: '3px solid rgba(254, 46, 129, 0.2)', borderTopColor: '#FE2E81', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <p style={{ margin: 0, fontSize: '0.9rem' }}>Loading weretradeIT Security Ferret...</p>
  </div>
);

// Navigation items matching sprite icons and application views
const navItems: NavItem[] = [
  { label: 'Overview', to: '/', icon: 'dashboard' },
  { label: 'Fleet CVEs', to: '/cve', icon: 'shield' },
  { label: 'Honeypot Intel', to: '/honeypot', icon: 'honeypot' },
  { label: 'New Scan', to: '/scan', icon: 'search' },
  { label: 'Results', to: '/results', icon: 'chart' },
  { label: 'History', to: '/history', icon: 'analytics' },
];

const navigation: NavigationConfig = { sections: [{ items: navItems }] };

// Route type for hash-based routing
type Route = "/" | "/cve" | "/scan" | "/results" | "/history" | "/honeypot";

export function App() {
  const [route, setRoute] = useState<Route>("/");
  const [scanId, setScanId] = useState<string | null>(null);
  const { loading, authenticated, user, logout } = useSecurityAuth();

  // Hash-based routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || "/";
      const [path, query] = hash.split("?");
      setRoute(path as Route);

      // Extract scanId from query params if present
      if (query) {
        const params = new URLSearchParams(query);
        const id = params.get("id");
        if (id) setScanId(id);
      }

      // Update page title
      const titles: Record<Route, string> = {
        "/": "Security Ferret — weretradeIT",
        "/cve": "Fleet CVE Intelligence — Security Ferret",
        "/scan": "New Scan — Security Ferret",
        "/results": "Scan Results — Security Ferret",
        "/history": "Scan History — Security Ferret",
        "/honeypot": "Honeypot Intelligence — Security Ferret",
      };
      document.title = titles[path as Route] || "Security Ferret — weretradeIT";
    };

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const navigate = (to: string) => {
    window.location.hash = to;
  };

  // Render page based on route
  const renderPage = () => {
    switch (route) {
      case "/cve":
        return <CveFleetPage />;
      case "/scan":
        return <ScanWizardPage />;
      case "/results":
        return <ScanResultsPage scanId={scanId} />;
      case "/history":
        return <ScanHistoryPage />;
      case "/honeypot":
        return <HoneypotDashboardPage />;
      default:
        return <HomePage />;
    }
  };

  const authUser = useMemo(() => {
    if (!authenticated || !user) return null;
    return {
      id: user.id,
      name: user.displayName || user.username,
      email: user.email || `${user.username}@weretrade.com`,
      role: user.role
    };
  }, [authenticated, user]);

  return (
    <>
      <IconSprite />
      <FleetChrome
        appKey="securityFerret"
        navigation={navigation}
        activePath={route}
        onNavigate={(item) => {
          if (item.to) navigate(item.to);
          else if (item.href) window.location.href = item.href;
        }}
        auth={{
          user: authUser,
          status: loading ? 'loading' : (authenticated ? 'authenticated' : 'unauthenticated'),
          logout: logout
        }}
        authBaseUrl="https://login.weretrade.com/api/auth"
        toolbar={
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(254, 46, 129, 0.1)', border: '1px solid rgba(254, 46, 129, 0.3)', padding: '0.2rem 0.65rem', borderRadius: '9999px', fontSize: '0.75rem' }}>
            <span style={{ color: '#22c55e', fontWeight: 700 }}>● LIVE</span>
            <span style={{ color: 'var(--text, #f8fafc)', fontWeight: 600 }}>security.weretrade.com</span>
            <span style={{ opacity: 0.6 }}>· ferret.weretrade.com</span>
          </div>
        }
      >
        <Suspense fallback={<PageLoader />}>
          {renderPage()}
        </Suspense>
      </FleetChrome>
    </>
  );
}
